import { isSignInWithEmailLink, signInWithEmailLink } from "firebase/auth";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";

import { auth } from "integrations/firebase/auth";
import { db } from "integrations/firebase/firestore";

// Firebase requires the address again when completing the link, and the link
// itself deliberately does not carry it — anyone who intercepts the email would
// otherwise have everything needed to sign in as that person. Keeping it here
// means the same browser completes the flow; a different one is asked to retype
// the address, which is the intended fallback rather than a failure.
const PENDING_ADDRESS_KEY = "connect:emailLinkAddress";

/**
 * THE CUTOVER, THROWN. The conditions next to it in git history
 * (pre-flip: branch `feat/sign-in-link-via-outbox`) all held when this
 * switched: the Workspace delegation had propagated, the recipient allowlist
 * covered the test addresses, and `authorized_domains` carried the vended
 * domain in both environments. The Firebase-sender branch was removed in the
 * same change that flipped this, deliberately: there is no third state
 * ("sends via Firebase, always works") to revert to without re-proving the
 * conditions, because returning to it would re-open an unauthenticated mail
 * relay wearing our identity — the thing the backend path exists to close.
 *
 * The constant stays as the frontend half of the two-file cutover switch:
 * email-link.test.ts reads both literals and fails if they disagree.
 */
const SIGN_IN_LINK_VIA_OUTBOX: boolean = true;

function continueUrl(): string {
  // Back to wherever they started, so an invitee following an event link lands
  // on that event rather than a dashboard.
  return `${window.location.origin}${continuePath()}`;
}

// What the backend appends to the continue URL to make it the sign-in link
// (sign_in_url_on_app_origin): the mailed link is our own page carrying these,
// and the SDK reads them off the address bar. They are the credential, so they
// never travel on into a new request or survive the completion.
const ACTION_PARAMS = ["apiKey", "oobCode", "mode", "lang", "tenantId"];

function continuePath(): string {
  // Path and query only. The backend composes this against its own origin and
  // refuses anything that could escape it, so sending an absolute URL would be
  // rejected and silently downgraded to the bare origin.
  const { pathname, search } = window.location;
  const query = new URLSearchParams(search);
  if (!ACTION_PARAMS.some((name) => query.has(name))) {
    return `${pathname}${search}`;
  }
  for (const name of ACTION_PARAMS) query.delete(name);
  const kept = query.toString();
  return `${pathname}${kept ? `?${kept}` : ""}`;
}

function rememberAddress(address: string): void {
  try {
    window.localStorage.setItem(PENDING_ADDRESS_KEY, address);
  } catch {
    // Private browsing, or storage disabled. The completion path asks for the
    // address instead, so this is a degraded flow rather than a broken one.
  }
}

/**
 * The invite (ADR-0003 as settled by D21): what a request may carry so a
 * stranger's passwordless sign-in can be admitted.
 *
 * Form accepts either the token itself or a full invite URL — a mint copied
 * straight out of the platform's share text is the common paste.
 */
export function extractInviteToken(
  raw: string | undefined | null,
): string | undefined {
  if (!raw) return undefined;
  const value = raw.trim();
  if (!value) return undefined;
  const match = /(?:^|[?&])invite=([^&]+)/.exec(value);
  if (match) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      // Hand-typed or truncated escape sequence; the backend's grammar is
      // the honest judge of what we send.
      return match[1];
    }
  }
  return value;
}

/** The invite the app was opened with — `/sign-in?invite=...`. */
export function inviteTokenFromLocation(): string | undefined {
  try {
    return (
      new URL(window.location.href).searchParams.get("invite") ?? undefined
    );
  } catch {
    return undefined;
  }
}

/**
 * Ask for a sign-in link. The one function the UI calls.
 *
 * `invite` carries the platform invite (ADR-0003 as settled by D21): a token
 * an event admin or a super admin minted. The backend judges it — presence on
 * a refused-domain address with no account, liveness, the slot it charges —
 * and its verdict travels back through `awaitInviteVerdict`. Without it this
 * is the plain screen, which admits addresses on the domain policy alone;
 * every stranger without an invite produces silence, by design.
 *
 * Returns the request document's id, which is the key to the verdict
 * document. Nothing about the mail itself is readable — the request
 * collection is create-only in firestore.rules.
 */
export async function sendSignInLinkRequest(
  email: string,
  invite?: string,
): Promise<string> {
  // The branch this replaced is gone; the constant records that the switch
  // was thrown deliberately rather than inferred.
  if (!SIGN_IN_LINK_VIA_OUTBOX) {
    throw new Error("sign-in link cutover is off — see email-link.ts history");
  }
  return requestSignInLinkViaOutbox(email, invite);
}

export async function requestSignInLinkViaOutbox(
  email: string,
  invite?: string,
): Promise<string> {
  const address = email.trim();
  const token = extractInviteToken(invite);
  const document: Record<string, unknown> = {
    email: address,
    // Must be the server's clock: the rule pins it to request.time, so a
    // client-supplied Date is rejected and cannot be backdated.
    requestedAt: serverTimestamp(),
    continuePath: continuePath(),
  };
  if (token) {
    // Exactly the field the rule bounds. A token and a URL are not half a
    // pair here: rules refuse anything that is not the token itself, so the
    // extraction happens before this call, not during it.
    document.inviteToken = token;
  }
  const created = await addDoc(
    collection(db, "sign_in_link_requests"),
    document,
  );
  rememberAddress(address);
  return created.id;
}

export type InviteVerdict = "accepted" | "rejected";

/**
 * Poll the verdict on an offered invite.
 *
 * The verdict document is computed from the token ALONE (see the comment on
 * the collection in firestore.rules), so "rejected" says the invite is
 * expired, withdrawn or full — never whether an account exists under the
 * address. A member re-offering a dead token is exactly why: they get asked
 * to fetch a fresh invite, and the mail that is already on its way to their
 * real account is not contradicted by anything this reports.
 *
 * Null on timeout: the most common causes (no token was carried, the request
 * was throttled, the backend is slow) all mean "no promised answer", and
 * silence is the honest rendering of that. The UI keeps showing its
 * "check your inbox" acknowledgement, which is also all it ever knew.
 */
export async function awaitInviteVerdict(
  requestId: string,
  {
    timeoutMs = 20000,
    intervalMs = 1500,
  }: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<InviteVerdict | null> {
  const deadline = Date.now() + timeoutMs;
  const reference = doc(db, "sign_in_link_invite_verdicts", requestId);
  // The first check waits too: the trigger runs a transaction that starts
  // with this write, and asking sooner would just be a failed read.
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
    const snapshot = await getDoc(reference);
    const verdict = (snapshot.data() as { verdict?: unknown } | undefined)
      ?.verdict;
    if (verdict === "accepted" || verdict === "rejected") return verdict;
  }
  return null;
}

export function isReturningFromEmailLink(): boolean {
  return isSignInWithEmailLink(auth, window.location.href);
}

export function rememberedAddress(): string | null {
  try {
    return window.localStorage.getItem(PENDING_ADDRESS_KEY);
  } catch {
    return null;
  }
}

/**
 * Complete a sign-in the user started by requesting a link.
 *
 * The domain gate still applies to everyone the domain policy admits, and —
 * under ADR-0003 as settled by D21 — everyone else rides the invite grant the
 * issuance wrote when their invite was live. Proving control of the mailbox
 * is what this adds, not permission to enter.
 */
export async function completeSignInFromLink(email: string): Promise<void> {
  await signInWithEmailLink(auth, email.trim(), window.location.href);
  try {
    window.localStorage.removeItem(PENDING_ADDRESS_KEY);
  } catch {
    // Nothing to clean up if storage was never available.
  }
  // Strip the credential out of the address bar so a shared or bookmarked URL
  // cannot be replayed, and so a reload does not re-run the completion.
  window.history.replaceState({}, "", continueUrl());
}

/**
 * Give up on the link in the address bar and go back to the plain form.
 *
 * For a link that can no longer complete: spent, expired, or opened after a
 * newer one was sent. Stripping the action parameters is what makes
 * `isReturningFromEmailLink` false again, so the form offers a fresh request
 * instead of re-running a completion that is certain to fail.
 */
export function abandonEmailLink(): void {
  window.history.replaceState({}, "", continueUrl());
}
